export const extractBase64String = (dataUrl) => {
  return dataUrl.split(";base64,")[1];
};

export const getMimeType = (dataUrl) => {
  return dataUrl.split(";")[0].split(":")[1];
};

export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const createImage = (url) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => {
      reject(error);
    });
    image.crossOrigin = "anonymous";
    image.src = url;
  });
};

export const calculateOptimalZoom = (mediaSize, cropSize = 400) => {
  const { naturalWidth, naturalHeight } = mediaSize;

  // Calculate the zoom needed to fit the smallest dimension to the crop area
  const minScale = Math.max(cropSize / naturalWidth, cropSize / naturalHeight);

  // Ensure minimum zoom is at least 1 and round to reasonable precision
  return Math.max(1, Math.round(minScale * 100) / 100);
};

async function loadSvgAsImage(url, width = 1000, height = 1000) {
  const res = await fetch(url);
  const svgText = await res.text();
  const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
  const svgUrl = URL.createObjectURL(svgBlob);

  const image = await createImage(svgUrl);
  image.width = width;
  image.height = height;
  return image;
}

/**
 * Resize and compress an image to WebP format with max dimension 1024px.
 * @param {File|string} imageFileOrDataUrl - The image File or data URL.
 * @param {number} maxSize - The max width or height (default 1024).
 * @param {number} quality - WebP quality (default 0.8).
 * @returns {Promise<Blob>} - The resized and compressed WebP Blob.
 */
export async function resizeAndCompressImage(
  imageFileOrDataUrl,
  maxSize = 2048,
  quality = 0.9,
  mimeType = "image/webp"
) {
  let dataUrl;
  if (typeof imageFileOrDataUrl === "string") {
    dataUrl = imageFileOrDataUrl;
  } else {
    // File object
    dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(imageFileOrDataUrl);
    });
  }
  const image = await createImage(dataUrl);
  let { width, height } = image;
  if (width > maxSize || height > maxSize) {
    const aspect = width / height;
    if (aspect > 1) {
      width = maxSize;
      height = Math.round(maxSize / aspect);
    } else {
      height = maxSize;
      width = Math.round(maxSize * aspect);
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // Improve image quality with better rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(image, 0, 0, width, height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/**
 * Crop an image and return a WebP Blob.
 * @param {string} imageSrc - The image data URL.
 * @param {object} pixelCrop - { x, y, width, height }
 * @param {number} quality - WebP quality (default 0.8)
 * @returns {Promise<Blob|null>} - The cropped WebP Blob or null on error.
 */
export async function getCroppedImg(imageSrc, pixelCrop, quality = 0.9) {
  try {
    let image = await createImage(imageSrc);
    if (
      imageSrc.endsWith(".svg") ||
      imageSrc.startsWith("data:image/svg+xml") ||
      imageSrc.includes("/svg")
    ) {
      image = await loadSvgAsImage(imageSrc, 1000, 1000); // Adjust resolution as needed
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.translate(image.width / 2, image.height / 2);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);
    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");
    if (!croppedCtx) {
      return null;
    }
    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;
    croppedCtx.drawImage(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
    return new Promise((resolve) => {
      croppedCanvas.toBlob((blob) => resolve(blob), "image/webp", quality);
    });
  } catch (error) {
    return error;
  }
}
